import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TacticalButton } from '@/components/ui/TacticalButton'
import { TacticalCard } from '@/components/ui/TacticalCard'
import type { DeviceLicense } from '@/hooks/useAdmin'
import { formatDate, formatLastSeen } from '@/hooks/useAdmin'

type DeviceListProps = {
  devices: DeviceLicense[]
  isLoading?: boolean
  onDeleteDevice: (deviceId: string) => void
  deleteError?: string | null
  onResetDeleteError?: () => void
  isDeleting?: boolean
}

export function DeviceList({ devices, isLoading, onDeleteDevice, deleteError, onResetDeleteError, isDeleting }: DeviceListProps) {
  const [deletingDevice, setDeletingDevice] = useState<DeviceLicense | null>(null)
  const wasDeleting = useRef(false)

  useEffect(() => {
    if (wasDeleting.current && !isDeleting && !deleteError) {
      setDeletingDevice(null)
    }
    wasDeleting.current = !!isDeleting
  }, [isDeleting, deleteError])

  const activeDevices = devices.filter((d) => d.status === 'active')
  const revokedDevices = devices.filter((d) => d.status === 'revoked')

  const handleDeleteClick = (device: DeviceLicense) => {
    onResetDeleteError?.()
    setDeletingDevice(device)
  }

  const handleConfirmDelete = () => {
    if (deletingDevice) {
      onDeleteDevice(deletingDevice.id)
    }
  }

  const handleCancelDelete = () => {
    setDeletingDevice(null)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (devices.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-sm text-muted-foreground/70">No devices registered</p>
      </div>
    )
  }

  return (
    <>
      <div className="space-y-6">
        {/* Active Devices */}
        {activeDevices.length > 0 && (
          <div className="space-y-3">
            <span className="text-xs text-muted-foreground/70">
              {activeDevices.length} active device{activeDevices.length !== 1 ? 's' : ''}
            </span>
            {activeDevices.map((device) => (
              <DeviceCard
                key={device.id}
                device={device}
                onDeleteClick={() => handleDeleteClick(device)}
              />
            ))}
          </div>
        )}

        {/* Revoked Devices */}
        {revokedDevices.length > 0 && (
          <div className="space-y-3">
            <span className="text-xs text-muted-foreground/70">
              {revokedDevices.length} revoked device{revokedDevices.length !== 1 ? 's' : ''}
            </span>
            {revokedDevices.map((device) => (
              <DeviceCard
                key={device.id}
                device={device}
                isRevoked
                onDeleteClick={() => handleDeleteClick(device)}
              />
            ))}
          </div>
        )}
      </div>

      {deletingDevice && (
        <DeleteDeviceDialog
          device={deletingDevice}
          isDeleting={isDeleting}
          error={deleteError}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// DeviceCard
// ---------------------------------------------------------------------------

type DeviceCardProps = {
  device: DeviceLicense
  isRevoked?: boolean
  onDeleteClick: () => void
}

function DeviceCard({
  device,
  isRevoked,
  onDeleteClick,
}: DeviceCardProps) {
  const getStatusText = () => {
    if (isRevoked || device.status === 'revoked') return 'revoked'
    if (!device.last_seen) return 'never seen'

    const lastSeen = new Date(device.last_seen)
    const daysSince = (Date.now() - lastSeen.getTime()) / (1000 * 60 * 60 * 24)

    if (daysSince > 90) return 'inactive'
    if (daysSince > 7) return 'offline'
    return 'online'
  }

  const getStatusColor = () => {
    const status = getStatusText()
    switch (status) {
      case 'online':
        return 'text-primary/70'
      case 'offline':
      case 'never seen':
        return 'text-yellow-500/70'
      case 'inactive':
      case 'revoked':
        return 'text-destructive/70'
      default:
        return 'text-muted-foreground/50'
    }
  }

  return (
    <TacticalCard
      className={cn(isRevoked && 'opacity-50')}
      variant={isRevoked ? 'muted' : 'default'}
      padding="default"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm text-foreground truncate">
              {device.hardware_serial || 'Unknown device'}
            </p>
            <span className={cn('text-xs', getStatusColor())}>
              {getStatusText()}
            </span>
          </div>
          <p className="text-xs text-muted-foreground/50 font-mono mt-1 truncate">
            {device.license_id}
          </p>
          <p className="text-xs text-muted-foreground/50 mt-1">
            Activated {formatDate(device.activated_at)} · Last seen {formatLastSeen(device.last_seen)}
          </p>
        </div>

        <div className="flex-shrink-0 flex items-center gap-2">
          <button
            onClick={onDeleteClick}
            className="text-xs text-destructive/70 hover:text-destructive transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </TacticalCard>
  )
}

// ---------------------------------------------------------------------------
// Delete confirmation dialog
// ---------------------------------------------------------------------------

type DeleteDeviceDialogProps = {
  device: DeviceLicense
  isDeleting?: boolean
  error?: string | null
  onConfirm: () => void
  onCancel: () => void
}

function DeleteDeviceDialog({ device, isDeleting, error, onConfirm, onCancel }: DeleteDeviceDialogProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) onCancel()
    },
    [isDeleting, onCancel]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isDeleting) onCancel()
      }}
    >
      <div className="w-full max-w-md mx-4 bg-background border border-white/[0.08] rounded-xl shadow-2xl animate-scale-in">
        <div className="p-6 space-y-4">
          {/* Warning icon */}
          <div className="w-12 h-12 mx-auto rounded-full bg-destructive/10 border border-destructive/20 flex items-center justify-center">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>

          {/* Title */}
          <div className="text-center">
            <h3 className="text-lg font-medium text-foreground">
              Permanently delete this device license?
            </h3>
          </div>

          {/* Device details */}
          <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] p-3 space-y-1">
            <p className="text-sm text-foreground">
              {device.hardware_serial || 'Unknown device'}
            </p>
            <p className="text-xs text-muted-foreground/50 font-mono truncate">
              {device.hardware_fingerprint}
            </p>
            <p className="text-xs text-muted-foreground/50">
              License: {device.license_id} · Status: {device.status} · Activated {formatDate(device.activated_at)}
            </p>
          </div>

          {/* Explanation */}
          <div className="rounded-lg bg-destructive/[0.04] border border-destructive/10 p-3 space-y-2">
            <p className="text-sm text-foreground/90 font-medium">
              What this does:
            </p>
            <ul className="text-xs text-muted-foreground/70 space-y-1.5 list-disc list-inside">
              <li>
                Permanently deletes the <span className="font-mono text-muted-foreground">device_licenses</span> row from the database
              </li>
              <li>
                Releases the seat, so another device can be activated in its place
              </li>
              <li>
                Does <span className="text-foreground/80">not</span> stop the device. If it is still running, it keeps working on its current license indefinitely &mdash; there is no way to switch it off remotely
              </li>
              <li>
                All record of this device activation will be lost (license ID, activation date, last seen timestamp)
              </li>
            </ul>
            <p className="text-xs text-destructive font-medium pt-1">
              This action cannot be undone. There is no way to restore a deleted device license.
            </p>
          </div>

          {/* When to use */}
          <div className="text-xs text-muted-foreground/50 space-y-1">
            <p className="font-medium text-muted-foreground/70">Use this when:</p>
            <ul className="list-disc list-inside space-y-0.5 ml-1">
              <li>Hardware is genuinely dead or unrecoverable, and the customer needs the seat back</li>
              <li>A device was activated by mistake</li>
            </ul>
            <p className="pt-1">
              A re-flashed or wiped device does <span className="text-foreground/80">not</span> need this &mdash; it can re-activate itself, and doing so costs no seat. Because deleting cannot stop a running device, ask for evidence it is unusable (a screenshot of the activation error, for example) before releasing a seat.
            </p>
          </div>
        </div>

        {error && (
          <div className="mx-6 rounded-lg bg-destructive/10 border border-destructive/20 px-3 py-2">
            <p className="text-xs text-destructive font-medium">Delete failed</p>
            <p className="text-xs text-destructive/80 mt-0.5">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/[0.06]">
          <TacticalButton
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={isDeleting}
          >
            Cancel
          </TacticalButton>
          <TacticalButton
            variant="destructive"
            size="sm"
            onClick={onConfirm}
            disabled={isDeleting}
            loading={isDeleting}
          >
            {error ? 'Retry' : 'Delete permanently'}
          </TacticalButton>
        </div>
      </div>
    </div>,
    document.body
  )
}
